import { z } from "zod";
import { ClientGenderEnum, ContracTypeEnum, SubscriptionMode } from "./clientEnum";
import moji from "moji";
import { isAnyKatakana } from "@/lib/utils";
import { isSanRole } from "@/features/auth/useAuthStore";

export const ClientSearchSchema = z.object({
  page: z.number().optional().catch(1),
  limit: z.number().optional().catch(10),
  clientName: z.string().optional(),
  clientNameKana: z.string().optional(),
});

export const ClientPathSchema = z.object({
  clientId: z.coerce.number().int(),
});

// 共通のベーススキーマ
const createBaseClientSchema = () => {
  return z.object({
    clientName: z
      .string()
      .min(1, "名義は必須です。")
      .transform((val) => {
        const removedSpace = val.replace(/[\s\u3000]+/g, "");
        return moji(removedSpace)
          .convert("HE", "ZE")
          .convert("HK", "ZK")
          .toString();
      }),
    clientNameKana: z
      .string()
      .min(1, "名義カナは必須です。")
      .transform((val) => {
        const removedSpace = val.replace(/[\s\u3000]+/g, "");
        return moji(removedSpace)
          .convert("HE", "ZE")
          .convert("HK", "ZK")
          .toString();
      })
      .refine((val) => isAnyKatakana(val), {
        message: "カタカナで入力してください",
      }),
    contractType: z.enum(ContracTypeEnum),
    gender: z.enum(ClientGenderEnum),
    birthDate: z.string(),
  });
};

// SANフィールドの必須バリデーション
const createRequiredSanFields = () => {
  return {
    email: z.string().email("メールアドレスが不正です。"),
    contactTel: z
      .string()
      .min(1, "連絡先は必須です。")
      .regex(/^\d+$/, "連絡先は数字のみで入力してください。")
      .max(13, "連絡先は13桁以内で入力してください。"),
    subscriptionMode: z.enum(Object.values(SubscriptionMode)),
  };
};

// SANフィールドのオプショナルバリデーション
const createOptionalSanFields = () => {
  return {
    email: z.string().optional(),
    contactTel: z
      .string()
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: "連絡先は数字のみで入力してください。",
      })
      .refine((val) => !val || val.length <= 13, {
        message: "連絡先は13桁以内で入力してください。",
      }),
    subscriptionMode: z.enum(Object.values(SubscriptionMode)).optional(),
  };
};

// 個人契約時のバリデーションチェック
const addIndividualContractCheck = <T extends z.ZodObject<any>>(schema: T): T => {
  return schema.superRefine((data, ctx) => {
    if (data.contractType == ContracTypeEnum.Individual) {
      if (!data.birthDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "生年月日は必須です。",
          path: ["birthDate"],
        });
      }
      if (!data.gender) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "性別は必須です。",
          path: ["gender"],
        });
      }
    }
  }) as T;
};

export const createClientCreateSchema = (adminRole?: string) => {
  const baseSchema = createBaseClientSchema();

  if (isSanRole(adminRole as any)) {
    return addIndividualContractCheck(
      baseSchema.extend(createRequiredSanFields())
    );
  }

  return addIndividualContractCheck(
    baseSchema.extend(createOptionalSanFields())
  );
};

// 後方互換性のため、デフォルトのスキーマも提供（adminRole未指定時はバリデーションなし）
export const ClientCreateSchema = createClientCreateSchema();

// 型定義（email, contactTel, subscriptionModeは常に存在するが、バリデーションが条件付き）
export type ClientCreateForm = z.infer<ReturnType<typeof createClientCreateSchema>>;

export const createClientUpdateSchema = (adminRole?: string) => {
  const baseSchema = createBaseClientSchema();

  if (isSanRole(adminRole as any)) {
    return addIndividualContractCheck(
      baseSchema.extend(createRequiredSanFields())
    );
  }

  return addIndividualContractCheck(
    baseSchema.extend(createOptionalSanFields())
  );
};

// 後方互換性のため、デフォルトのスキーマも提供（adminRole未指定時はバリデーションなし）
export const ClientUpdateSchema = createClientUpdateSchema();

// 型定義
export type ClientUpdateForm = z.infer<ReturnType<typeof createClientUpdateSchema>>;
